import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, X } from 'lucide-react';
import { apiClient } from '@/api/Client';
import { PasswordInput } from '@/components/Ui/PasswordInput';

// SEC-ROOM-PRESERVE-01: ritual de desativacao de sala. Nenhuma sala e
// excluida; a desativacao preserva a sala e todos os registros. Operacao
// real exige uma segunda pessoa autorizada para concluir (backend decide).
export const REAL_OPERATION_CONFIRMATION_PHRASE = 'DESATIVAR OPERAÇÃO REAL';
const MIN_REASON_LENGTH = 20;

const ERROR_MESSAGES = {
  confirmation_name_mismatch: 'O nome digitado não confere com o nome da sala.',
  reason_required: `Descreva o motivo com pelo menos ${MIN_REASON_LENGTH} caracteres.`,
  confirmation_phrase_required: `Digite a frase de confirmação: ${REAL_OPERATION_CONFIRMATION_PHRASE}`,
  reauthentication_failed: 'Senha atual incorreta (ou senha provisória ainda não trocada).',
  second_approver_required: 'Você já solicitou esta desativação. A aprovação precisa ser de outra pessoa autorizada.',
  room_already_deactivated: 'Esta sala já está desativada.',
  native_room_protected: 'A sala nativa do sistema não pode ser desativada.',
  forbidden: 'Somente a plataforma ou o titular da instituição da sala podem desativar salas.',
  room_persistence_unavailable: 'Banco indisponível. Nada foi alterado; tente novamente.',
};

function activePending(room) {
  const pending = room?.metadata?.lifecycle?.pendingDeactivation;
  if (!pending?.requestedBy || !(Date.parse(pending.expiresAt) > Date.now())) return null;
  return pending;
}

function isMine(pending, actor) {
  if (!pending) return false;
  if (pending.requestedBy.userId && actor?.id != null) return pending.requestedBy.userId === String(actor.id);
  return Boolean(actor?.operatorId) && pending.requestedBy.operatorId === actor.operatorId;
}

const field = 'mt-1 w-full rounded border border-red-400/30 bg-black/60 px-3 py-2 text-sm text-red-50 outline-none focus:border-red-300';

export default function RoomDeactivationDialog({ room, actor, onClose, onCompleted }) {
  const expectedName = String(room?.name || '').trim() || room?.operationId;
  const pending = activePending(room);
  const [realOperation, setRealOperation] = useState(room?.metadata?.operationType === 'real_operation' || Boolean(pending));
  const [form, setForm] = useState({ confirmName: '', reason: '', confirmPhrase: '', currentPassword: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = key => event => setForm(current => ({ ...current, [key]: event.target.value }));

  const pendingIsMine = isMine(pending, actor);
  const approving = Boolean(pending) && !pendingIsMine;
  const ready = form.confirmName.trim() === expectedName
    && form.reason.trim().length >= MIN_REASON_LENGTH
    && form.currentPassword
    && (!realOperation || form.confirmPhrase.trim());

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient.post(`/api/rooms/${encodeURIComponent(room.operationId)}/deactivation`, form);
      onCompleted?.(result);
    } catch (err) {
      if (err.code === 'confirmation_phrase_required') setRealOperation(true);
      setError(ERROR_MESSAGES[err.code] || err.message || 'Falha ao desativar a sala.');
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient.post(`/api/rooms/${encodeURIComponent(room.operationId)}/deactivation/cancel`, {});
      onCompleted?.(result);
    } catch (err) {
      setError(ERROR_MESSAGES[err.code] || err.message || 'Falha ao cancelar o pedido.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" aria-labelledby="room-deactivation-title"
      onClick={(event) => event.stopPropagation()}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-red-500/50 bg-slate-950 p-5 text-sm text-slate-100 shadow-[0_0_40px_rgba(239,68,68,0.25)]">
        <div className="flex items-start justify-between gap-3">
          <h2 id="room-deactivation-title" className="flex items-center gap-2 text-base font-bold uppercase tracking-wider text-red-300">
            <ShieldAlert size={18} /> {approving ? 'Aprovar desativação' : 'Desativar sala'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" className="rounded p-1 text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <p className="mt-3 font-semibold text-red-100">{expectedName} <span className="font-normal text-slate-400">({room.operationId})</span></p>
        {realOperation && <p className="mt-2 rounded border border-red-500/60 bg-red-500/15 px-3 py-2 text-xs font-bold uppercase tracking-wider text-red-200">Operação real</p>}

        <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-slate-300">
          <li>A sala <strong>não é excluída</strong>: ela sai da lista e deixa de abrir, mas a sala e todos os registros continuam preservados.</li>
          <li>Pode ser reativada depois pela plataforma ou pelo titular da instituição.</li>
          <li>Seu nome, o horário e o motivo ficam registrados de forma permanente.</li>
          {realOperation && <li><strong>Operação real exige duas pessoas:</strong> uma solicita e outra pessoa autorizada aprova. Sem a segunda aprovação em 72 horas, o pedido expira e a sala continua ativa.</li>}
        </ul>

        {pendingIsMine ? (
          <div className="mt-4 space-y-3">
            <p className="rounded border border-amber-400/50 bg-amber-500/10 p-3 text-xs text-amber-100">
              Você solicitou esta desativação. Ela aguarda a aprovação de outra pessoa autorizada até {new Date(pending.expiresAt).toLocaleString('pt-BR')}.
            </p>
            {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded border border-slate-500/50 px-3 py-2 text-xs">Fechar</button>
              <button type="button" disabled={busy} onClick={cancelPending} className="rounded border border-amber-400/60 px-3 py-2 text-xs text-amber-100 disabled:opacity-50">Cancelar pedido</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            {approving && (
              <p className="rounded border border-amber-400/50 bg-amber-500/10 p-3 text-xs text-amber-100">
                Solicitado por <strong>{pending.requestedBy.name || pending.requestedBy.operatorId}</strong> em {new Date(pending.requestedAt).toLocaleString('pt-BR')}.<br />
                Motivo: {pending.reason}
              </p>
            )}
            <label className="block text-xs">Digite o nome exato da sala: <strong className="text-red-200">{expectedName}</strong>
              <input className={field} value={form.confirmName} onChange={set('confirmName')} autoComplete="off" spellCheck={false} />
            </label>
            <label className="block text-xs">{approving ? 'Motivo da sua aprovação' : 'Motivo da desativação'} (mínimo {MIN_REASON_LENGTH} caracteres)
              <textarea className={field} rows={3} value={form.reason} onChange={set('reason')} />
            </label>
            {realOperation && (
              <label className="block text-xs">Digite a frase: <strong className="text-red-200">{REAL_OPERATION_CONFIRMATION_PHRASE}</strong>
                <input className={field} value={form.confirmPhrase} onChange={set('confirmPhrase')} autoComplete="off" />
              </label>
            )}
            <label className="block text-xs">Sua senha atual
              <PasswordInput className={field} value={form.currentPassword} onChange={set('currentPassword')} autoComplete="current-password"
                toggleClassName="border-red-400/30 text-red-200 hover:bg-red-500/10" />
            </label>
            {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="rounded border border-slate-500/50 px-3 py-2 text-xs">Cancelar</button>
              <button type="submit" disabled={!ready || busy} className="rounded border border-red-400/70 bg-red-600/30 px-3 py-2 text-xs font-bold uppercase tracking-wider text-red-100 disabled:opacity-40">
                {busy ? 'Processando…' : approving ? 'Aprovar desativação' : realOperation ? 'Solicitar desativação' : 'Desativar sala'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
